import type {
  DeviceConnectivity,
  DeviceReleaseStatus,
} from "@led-signage/shared/device-api";
import type {
  DeviceReleaseProgress,
  DisplayDetail,
  LatestScreenshotResponse,
} from "@led-signage/shared/management-api";
import { useEffect, useState } from "react";
import { newIdempotencyKey } from "./api/client.js";
import { describeFailure, type ActionFailure } from "./api/errors.js";
import { AuthenticationRecoveryButton } from "./AuthenticationRecoveryButton.js";
import { api } from "./api/index.js";
import { useResource } from "./api/useResource.js";
import { PermissionNotice, useCan } from "./session.js";

/**
 * 端末が「いま何を持っていて、何を映しているか」を見る画面。
 *
 * 配信版の取得は端末側の進行であり、クラウドから見えるのは端末が報告した
 * 分だけである。報告が無い状態と、取得に失敗した状態を混ぜない。
 */

const STATUS_LABELS: Record<DeviceReleaseStatus, string> = {
  assigned: "割り当て済み",
  downloading: "取得中",
  verifying: "照合中",
  ready: "適用待ち",
  active: "適用中",
  failed: "取得に失敗",
  rolled_back: "巻き戻し済み",
};

/** 進行中・完了・異常で色を分ける。異常だけ目立たせる。 */
const STATUS_TONE: Record<DeviceReleaseStatus, string> = {
  assigned: "pending",
  downloading: "pending",
  verifying: "pending",
  ready: "pending",
  active: "ok",
  failed: "warn",
  rolled_back: "warn",
};

function timestamp(value: string | null): string {
  if (value === null) return "—";
  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date(value));
}

function megabytes(bytes: number): string {
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

const CONNECTIVITY_LABELS: Record<DeviceConnectivity, string> = {
  online: "通信中",
  offline: "通信なし",
  unknown: "未通信",
};

function lastSeen(value: string): string {
  return `最終通信 ${timestamp(value)}`;
}

export function DeviceState() {
  const displays = useResource((signal) => api.listDisplays(signal), []);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  /*
   * 取得状況と画面は同じ端末の状態なので、まとめて取り直す。片方だけ新しいと、
   * どちらが現在の姿なのか読み手が判断できない。
   */
  const [reloadToken, setReloadToken] = useState(0);
  const reload = () => {
    setReloadToken((token) => token + 1);
  };

  return (
    <section className="panel device-state-panel">
      <h3>端末の状態を確認</h3>
      <p className="resource-note">
        配信版の取得状況と、端末が最後に送ってきた画面を確認します。
      </p>

      {displays.state === "loading" && (
        <p className="resource-note">LEDの一覧を読み込み中…</p>
      )}
      {displays.state === "error" && (
        <div className="inline-alert inline-alert--error">
          <b>LEDの一覧を読み込めませんでした</b>
          <span>{displays.message}</span>
        </div>
      )}
      {displays.state === "ready" && (
        <DevicePicker
          rows={displays.value.items}
          selected={deviceId}
          onSelect={setDeviceId}
        />
      )}

      {deviceId !== null && (
        <>
          <ReleaseProgress
            deviceId={deviceId}
            reloadToken={reloadToken}
            onReload={reload}
            key={`rel-${deviceId}`}
          />
          <LatestScreenshot
            deviceId={deviceId}
            reloadToken={reloadToken}
            onReload={reload}
            key={`shot-${deviceId}`}
          />
        </>
      )}
    </section>
  );
}

/**
 * 端末を選ぶ。
 *
 * 端末が未登録のLEDも隠さずに出す。一覧から消すと「LEDが無い」のか
 * 「端末をまだ登録していない」のか区別できなくなる。
 */
function DevicePicker({
  rows,
  selected,
  onSelect,
}: {
  rows: readonly DisplayDetail[];
  selected: string | null;
  onSelect: (deviceId: string) => void;
}) {
  if (rows.length === 0) {
    return (
      <p className="resource-note">
        登録されているLEDがありません。先にLEDと端末を登録してください。
      </p>
    );
  }

  return (
    <ul className="device-picker">
      {rows.map((row) => {
        const device = row.device;
        const status = row.deviceStatus;
        return (
          <li key={row.display.id}>
            <button
              type="button"
              className={
                device !== null && device.id === selected ? "active" : ""
              }
              disabled={device === null}
              onClick={() => {
                if (device !== null) onSelect(device.id);
              }}
            >
              <span className="device-picker__name">
                <b>{row.display.name}</b>
                <small>{device?.name ?? "端末が未登録です"}</small>
              </span>
              {status !== null && (
                <span
                  className={`state-tag state-tag--${
                    status.connectivity === "online" ? "ok" : "warn"
                  }`}
                >
                  {CONNECTIVITY_LABELS[status.connectivity]}
                </span>
              )}
              {status !== null && (
                <small className="device-picker__seen">
                  {lastSeen(status.lastSeenAt)}
                </small>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function ReleaseProgress({
  deviceId,
  reloadToken,
  onReload,
}: {
  deviceId: string;
  reloadToken: number;
  onReload: () => void;
}) {
  const progress = useResource(
    (signal) => api.getDeviceReleaseStatus(deviceId, signal),
    [deviceId, reloadToken],
  );

  if (progress.state === "loading") {
    return <p className="resource-note">取得状況を読み込み中…</p>;
  }
  if (progress.state === "error") {
    return (
      <div className="inline-alert inline-alert--error">
        <b>取得状況を読み込めませんでした</b>
        <span>{progress.message}</span>
        {progress.code === "NOT_FOUND" && (
          <span>端末IDが存在しないか、別の組織のものです。</span>
        )}
      </div>
    );
  }

  const rows = progress.value;
  if (rows.length === 0) {
    return (
      <div className="inline-alert">
        <b>この端末はまだ何も報告していません</b>
        <span>
          端末が存在しないという意味ではありません。配信版がまだ割り当てられて
          いないか、端末が一度も通信していない状態です。
        </span>
      </div>
    );
  }

  return (
    <div className="release-progress">
      <div className="latest-screenshot__head">
        <h4>配信版の取得状況</h4>
        <button type="button" className="secondary-button" onClick={onReload}>
          取り直す
        </button>
      </div>
      <table className="prep-table">
        <thead>
          <tr>
            <th>配信版</th>
            <th>状態</th>
            <th>取得量</th>
            <th>最終更新（JST）</th>
            <th>適用日時</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.releaseId}>
              <td>
                <code>{row.releaseId}</code>
              </td>
              <td>
                <span className={`state-tag state-tag--${STATUS_TONE[row.status]}`}>
                  {STATUS_LABELS[row.status]}
                </span>
              </td>
              <td>{megabytes(row.bytesDownloaded)}</td>
              <td>{timestamp(row.updatedAt)}</td>
              <td>{timestamp(row.activatedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.map((row) => <FailureNote row={row} key={row.releaseId} />)}
      <p className="resource-note">
        <b>「適用中」は配信版が端末に入って有効になったという意味で、映像が
        実際に流れていることまでは示しません。</b>再生の可否は別に確認して
        ください。
      </p>
      <p className="resource-note">
        ここに出るのは端末が報告した内容です。端末が通信できていない間は、
        実際の状態が変わっていても更新されません。
      </p>
    </div>
  );
}

function FailureNote({ row }: { row: DeviceReleaseProgress }) {
  if (row.status !== "failed") return null;
  return (
    <div className="inline-alert inline-alert--error">
      <b>{row.releaseId} の取得に失敗しています</b>
      <span>
        {row.errorMessage ?? "端末からの詳細はありません。"}
        {row.errorCode !== null && <code> {row.errorCode}</code>}
      </span>
    </div>
  );
}

type RequestState =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "requested"; expiresAt: string }
  | { kind: "failed"; failure: ActionFailure };

function LatestScreenshot({
  deviceId,
  reloadToken,
  onReload,
}: {
  deviceId: string;
  reloadToken: number;
  onReload: () => void;
}) {
  /*
   * 撮影は端末が次に通信したときに行われる。押した直後には届かないので、
   * 取り直す手段を用意する。要求後はしばらく自動で取り直し、届いたら止める。
   */
  const screenshot = useResource(
    (signal) => api.getLatestScreenshot(deviceId, signal),
    [deviceId, reloadToken],
  );
  const [request, setRequest] = useState<RequestState>({ kind: "idle" });
  const [waiting, setWaiting] = useState(false);
  const allowed = useCan("device:manage");

  const capturedAt =
    screenshot.state === "ready" ? screenshot.value.screenshot.capturedAt : null;

  /*
   * 要求してから届くまでの待ち時間は、ほぼheartbeatの間隔（最大30秒）である。
   * 10秒ごとに3分まで取り直し、それ以上は諦めて手動に任せる。無期限に叩き
   * 続けない。
   */
  useEffect(() => {
    if (!waiting) return undefined;
    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - startedAt > 3 * 60 * 1000) {
        setWaiting(false);
        return;
      }
      onReload();
    }, 10 * 1000);
    return () => {
      clearInterval(timer);
    };
  }, [waiting, onReload]);

  // 新しい画像が届いたら待つのをやめる。
  const [awaitedFrom, setAwaitedFrom] = useState<string | null>(null);
  useEffect(() => {
    if (waiting && capturedAt !== null && capturedAt !== awaitedFrom) {
      setWaiting(false);
      // 届いたものを待てと言い続けない。
      setRequest({ kind: "idle" });
    }
  }, [waiting, capturedAt, awaitedFrom]);

  async function capture() {
    setRequest({ kind: "running" });
    try {
      const created = await api.requestScreenshot(deviceId, newIdempotencyKey());
      setRequest({ kind: "requested", expiresAt: created.expiresAt });
      setAwaitedFrom(capturedAt);
      setWaiting(true);
    } catch (error) {
      setRequest({ kind: "failed", failure: describeFailure(error) });
    }
  }

  return (
    <div className="latest-screenshot">
      <div className="latest-screenshot__head">
        <h4>最後に届いた画面</h4>
        <button type="button" className="secondary-button" onClick={onReload}>
          {waiting ? "届くのを待っています…" : "取り直す"}
        </button>
      </div>

      {screenshot.state === "loading" && (
        <p className="resource-note">読み込み中…</p>
      )}
      {screenshot.state === "error" &&
        (screenshot.code === "NOT_FOUND" ? (
          <p className="resource-note">
            この端末から届いた画面はまだありません。
          </p>
        ) : (
          <div className="inline-alert inline-alert--error">
            <b>読み込めませんでした</b>
            <span>{screenshot.message}</span>
          </div>
        ))}
      {screenshot.state === "ready" && <ScreenshotView value={screenshot.value} />}

      <PermissionNotice permission="device:manage" action="撮影の要求" />

      {request.kind === "failed" && (
        <div className="inline-alert inline-alert--error">
          <b>要求できませんでした</b>
          <span>{request.failure.message}</span>
          <AuthenticationRecoveryButton failure={request.failure} />
        </div>
      )}

      {request.kind === "requested" ? (
        <div className="inline-alert">
          <b>撮影を要求しました</b>
          <span>
            要求は端末が次に通信したときに渡されます（通常30秒以内）。
            {timestamp(request.expiresAt)}
            までに端末が受け取らなければ、この要求は期限切れになります。
          </span>
          <span>
            画像が届くのは端末が撮影して送り終えた後です。届くまで自動で
            取り直します。
          </span>
        </div>
      ) : (
        <div className="editor-actions">
          <button
            type="button"
            disabled={!allowed || request.kind === "running"}
            onClick={() => void capture()}
          >
            {request.kind === "running" ? "要求中…" : "いまの画面を撮影させる"}
          </button>
        </div>
      )}

      <p className="resource-note">
        待ち時間のほとんどは端末が次に通信するまでの間です。通信してからは
        数秒で届きます。端末が通信できていない間は要求が渡らず、期限切れに
        なります。
      </p>
    </div>
  );
}

function ScreenshotView({ value }: { value: LatestScreenshotResponse }) {
  /*
   * downloadUrl は署名付きの一時URLで、bearer tokenと同じ重みを持つ。
   * 表示にだけ使い、控えを残さない。有効期限を過ぎたら読み込みは失敗する。
   */
  return (
    <figure className="screenshot-view">
      <img
        src={value.downloadUrl}
        alt={`端末が ${timestamp(value.screenshot.capturedAt)} に撮影した画面`}
      />
      <figcaption>
        撮影 {timestamp(value.screenshot.capturedAt)} / 受信{" "}
        {timestamp(value.screenshot.receivedAt)}
        <br />
        <small>
          表示用のURLは {timestamp(value.downloadUrlExpiresAt)} に切れます。
          共有せず、必要なら画面を開き直してください。
        </small>
      </figcaption>
    </figure>
  );
}
